import { useState } from "react";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const PROJECT_TYPES = [
  "Neue Website",
  "Bestehende Website verbessern",
  "Buchungssystem einrichten",
  "Automatisierung umsetzen",
];

export default function AnalysisForm() {
  const [values, setValues] = useState({
    service: "Neue Website",
    hasWebsite: "nein",
    name: "",
    company: "",
    website: "",
    email: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({
      ...v,
      [name]: value,
      // Eine zuvor eingetragene URL darf beim Wechsel auf „Nein“ nicht mitgesendet werden.
      ...(name === "hasWebsite" && value === "nein" ? { website: "" } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setError("");
    try {
      const formData = new FormData(e.currentTarget);
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });
      if (!res.ok) {
        throw new Error("Die Anfrage konnte nicht übermittelt werden.");
      }
      setStatus("success");
      setValues({ service: "Neue Website", hasWebsite: "nein", name: "", company: "", website: "", email: "", phone: "", message: "" });
    } catch {
      setStatus("error");
      setError(
        "Die Anfrage konnte nicht übermittelt werden. Bitte versuchen Sie es später erneut oder schreiben Sie direkt an kontakt@webdesign-damerow.de.",
      );
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="analysis-success"
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center"
      >
        <div className="flex justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white">
            <CheckCircle2 className="h-6 w-6" />
          </div>
        </div>
        <h3 data-testid="analysis-success-heading" className="mt-4 text-xl font-bold text-slate-900">
          Vielen Dank! Ihre Anfrage wurde erfolgreich übermittelt.
        </h3>
        <p className="mt-2 text-slate-600">
          Ich melde mich schnellstmöglich bei Ihnen.
        </p>
        <Button
          data-testid="analysis-reset"
          variant="outline"
          className="mt-6 rounded-full"
          onClick={() => setStatus("idle")}
        >
          Neue Anfrage senden
        </Button>
      </div>
    );
  }

  return (
    <form
      name="website-check"
      method="POST"
      data-netlify="true"
      netlify-honeypot="bot-field"
      data-testid="analysis-form"
      onSubmit={handleSubmit}
      className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm"
    >
      <input type="hidden" name="form-name" value="website-check" />
      <div className="hidden" aria-hidden="true">
        <Label htmlFor="af-bot-field">Dieses Feld bitte nicht ausfüllen</Label>
        <input
          id="af-bot-field"
          name="bot-field"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          data-testid="analysis-honeypot"
        />
      </div>
      <div className="flex items-center gap-2 text-[#2563EB] mb-5">
        <Sparkles className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-[0.18em]">
          Kostenloses Erstgespräch
        </span>
      </div>
      <p data-testid="analysis-form-intro" className="mb-5 text-sm leading-relaxed text-slate-600">
        Auch ohne bestehende Website oder fertigen Firmennamen können Sie uns kontaktieren. Ein paar Angaben zu Ihrem Vorhaben reichen.
      </p>

      <fieldset className="mb-7">
        <legend className="mb-3 text-sm font-semibold text-slate-800">
          Was möchten Sie umsetzen? <span className="text-[#2563EB]" aria-hidden="true">*</span>
        </legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {PROJECT_TYPES.map((option) => (
            <label
              key={option}
              className={
                "flex min-h-[64px] cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-colors " +
                (values.service === option
                  ? "border-[#2563EB] bg-blue-50 text-slate-900 shadow-sm"
                  : "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-slate-50")
              }
            >
              <input
                type="radio"
                name="service"
                value={option}
                checked={values.service === option}
                onChange={handleChange}
                required
                className="h-4 w-4 shrink-0 accent-[#2563EB]"
              />
              <span className="text-sm font-medium leading-5">{option}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="mb-7">
        <legend className="mb-3 text-sm font-semibold text-slate-800">
          Haben Sie bereits eine Website?
        </legend>
        <div className="flex flex-wrap gap-3">
          {[
            { value: "ja", label: "Ja" },
            { value: "nein", label: "Nein" },
          ].map((option) => (
            <label
              key={option.value}
              className={
                "inline-flex min-h-11 min-w-[110px] cursor-pointer items-center gap-3 rounded-xl border px-4 py-2.5 transition-colors " +
                (values.hasWebsite === option.value
                  ? "border-[#2563EB] bg-blue-50 font-semibold text-slate-900"
                  : "border-slate-200 text-slate-700 hover:border-blue-300")
              }
            >
              <input
                type="radio"
                name="hasWebsite"
                value={option.value}
                checked={values.hasWebsite === option.value}
                onChange={handleChange}
                className="h-4 w-4 accent-[#2563EB]"
              />
              <span className="text-sm">{option.label}</span>
            </label>
          ))}
        </div>
        {values.hasWebsite === "ja" && (
          <div className="mt-4">
            <Label htmlFor="af-website">Ihre Website-Adresse (optional)</Label>
            <Input
              id="af-website"
              name="website"
              type="url"
              inputMode="url"
              data-testid="analysis-website"
              maxLength={300}
              autoComplete="url"
              value={values.website}
              onChange={handleChange}
              placeholder="https://ihre-website.de"
              className="mt-1.5 h-11"
            />
            <p className="mt-2 text-xs text-slate-500">Sie können den Link auch später nachreichen.</p>
          </div>
        )}
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="af-name">Ihr Name*</Label>
          <Input
            id="af-name"
            name="name"
            data-testid="analysis-name"
            required
            maxLength={120}
            autoComplete="name"
            value={values.name}
            onChange={handleChange}
            placeholder="Ihr Name"
            className="mt-1.5 h-11"
          />
        </div>
        <div>
          <Label htmlFor="af-email">Ihre E-Mail*</Label>
          <Input
            id="af-email"
            name="email"
            type="email"
            data-testid="analysis-email"
            required
            maxLength={254}
            autoComplete="email"
            value={values.email}
            onChange={handleChange}
            placeholder="ihre@email.de"
            className="mt-1.5 h-11"
          />
        </div>
        <div>
          <Label htmlFor="af-company">Unternehmen oder Projekt (optional)</Label>
          <Input
            id="af-company"
            name="company"
            data-testid="analysis-company"
            maxLength={160}
            autoComplete="organization"
            value={values.company}
            onChange={handleChange}
            placeholder="Falls schon vorhanden"
            className="mt-1.5 h-11"
          />
        </div>
        <div>
          <Label htmlFor="af-phone">Telefon (optional)</Label>
          <Input
            id="af-phone"
            name="phone"
            data-testid="analysis-phone"
            value={values.phone}
            type="tel"
            maxLength={60}
            autoComplete="tel"
            onChange={handleChange}
            placeholder="030 …"
            className="mt-1.5 h-11"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="af-message">Was möchten Sie erreichen? (optional)</Label>
          <Textarea
            id="af-message"
            name="message"
            data-testid="analysis-message"
            value={values.message}
            maxLength={2000}
            onChange={handleChange}
            placeholder="Zum Beispiel: Ich gründe gerade einen Betrieb und brauche eine kleine Website …"
            className="mt-1.5 min-h-[92px]"
          />
        </div>
      </div>

      {status === "error" && (
        <p data-testid="analysis-error" role="alert" className="mt-4 text-sm text-red-600">
          {error}
        </p>
      )}

      <Button
        type="submit"
        data-testid="analysis-submit"
        disabled={status === "loading"}
        className="mt-6 w-full sm:w-auto rounded-full h-12 px-7 bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold"
      >
        {status === "loading" ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Wird gesendet …
          </>
        ) : (
          "Unverbindlich anfragen"
        )}
      </Button>
      <p data-testid="analysis-next-step" className="mt-4 text-sm leading-relaxed text-slate-500">
        Nach Ihrer Anfrage stimmen wir gemeinsam das weitere Vorgehen und bei Bedarf einen Gesprächstermin ab.
      </p>
      <p data-testid="analysis-free-note" className="mt-2 text-xs leading-relaxed text-slate-400">
        Die erste Anfrage und das Erstgespräch sind kostenlos und unverbindlich. Eine Umsetzung erfolgt erst nach Ihrer Beauftragung.
      </p>
      <p data-testid="analysis-privacy-notice" className="mt-3 text-xs text-slate-400">
        Ihre Angaben werden ausschließlich zur Bearbeitung Ihrer Anfrage verwendet. Weitere Informationen finden Sie in der{" "}
        <a
          href="/datenschutz"
          data-testid="analysis-privacy-link"
          className="font-medium text-slate-600 underline underline-offset-2 hover:text-slate-900"
        >
          Datenschutzerklärung
        </a>.
      </p>
    </form>
  );
}
